import React from 'react';
import {
    BusinessDetails,
    GoogleDetails,
    MenuType,
    SocialMediaProfilesTypes,
} from '../../../types';
import SocialFooterBlock from './social-footer-block/SocialFooterBlock';
import {
    footerLayoutTokens,
    footerStyles,
    footerThemeTokens,
} from './Footer.tokens';

type FooterProps = {
    Link: React.ComponentType<{
        href: string;
        title?: string;
        className?: string;
        style?: React.CSSProperties;
        children: React.ReactNode;
    }>;
    businessName: string;
    businessDetails: BusinessDetails;
    footerData: MenuType[];
    businessTitle?: string;
    socialMediaProfiles: SocialMediaProfilesTypes;
    googleDetails: GoogleDetails;
    hideSocialBlock: boolean;
    theme?: keyof typeof footerThemeTokens;
    layout?: keyof typeof footerLayoutTokens;
};

const normalizeHref = (href: string): string => {
    if (!href) return '#';
    if (
        href.startsWith('http://') ||
        href.startsWith('https://') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:')
    ) {
        return href;
    }
    return `/${href.replace(/^\/+/, '')}`;
};

const Footer = ({
    Link,
    businessName,
    businessDetails,
    socialMediaProfiles,
    googleDetails,
    footerData,
    theme = 'light',
    businessTitle,
    hideSocialBlock = false,
    layout = 'lg',
}: FooterProps) => {
    const tokens = footerThemeTokens[theme];
    const layoutTokens = footerLayoutTokens[layout];
    const footerLogoUrl = businessDetails?.branding?.footerLogoUrl;
    const year = new Date().getFullYear();

    const navigationalColumns = Array.isArray(footerData) ? footerData : [];
    const topBlockCount = navigationalColumns.length + (footerLogoUrl ? 1 : 0);
    const desktopColumns = Math.max(
        1,
        Math.min(layoutTokens.desktopMaxColumns, topBlockCount || 1)
    );

    return (
        <footer
            style={footerStyles.footer}
            className="footer-root"
            data-footer-theme={theme}
        >
            <style>{`
                .footer-root, .footer-root * { box-sizing: border-box; }
                .footer-root a { text-decoration: none; }
                .footer-root img { display: block; max-width: 100%; }
                .footer-top-grid {
                    grid-template-columns: minmax(0, 1fr);
                }
                .footer-bottom-inner {
                    flex-direction: column;
                    align-items: flex-start;
                }
                .footer-link:hover,
                .footer-link:focus-visible,
                .footer-social-link:hover,
                .footer-social-link:focus-visible {
                    color: var(--footer-link-hover) !important;
                    outline: none;
                }
                .footer-social-icon {
                    width: 16px;
                    height: 16px;
                    flex-shrink: 0;
                }
                @media (min-width: 640px) {
                    .footer-top-grid {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }
                    .footer-bottom-inner {
                        flex-direction: row;
                        align-items: center;
                        justify-content: space-between;
                    }
                }
                @media (min-width: 1024px) {
                    .footer-top-grid {
                        grid-template-columns: repeat(var(--footer-desktop-columns), minmax(0, 1fr));
                    }
                }
            `}</style>

            <div style={tokens.top}>
                <div style={{ ...layoutTokens.container, ...layoutTokens.topPadding }}>
                    <div style={footerStyles.topInner}>
                        <div
                            className="footer-top-grid"
                            style={{
                                ...footerStyles.columnsGrid,
                                ['--footer-desktop-columns' as string]: String(desktopColumns),
                                ['--footer-link-hover' as string]: tokens.linkHoverColor,
                            }}
                        >
                            {navigationalColumns.map((col, colIndex) => (
                                <div key={`${String(col.label ?? 'footer-column')}-${colIndex}`} style={footerStyles.column}>
                                    {col.label ? (
                                        <div
                                            style={{
                                                ...footerStyles.title,
                                                color: tokens.titleColor,
                                            }}
                                        >
                                            {col.label}
                                        </div>
                                    ) : null}

                                    {(col.children ?? []).map((item, itemIndex) => (
                                        <Link
                                            key={`${String(item.label ?? item.href ?? 'footer-link')}-${itemIndex}`}
                                            href={normalizeHref(item.href)}
                                            title={item.label}
                                            className="footer-link"
                                            style={{
                                                ...footerStyles.link,
                                                color: tokens.linkColor,
                                            } as React.CSSProperties}
                                        >
                                            {item.label}
                                        </Link>
                                    ))}
                                </div>
                            ))}

                            {footerLogoUrl ? (
                                <div style={footerStyles.brandBlock}>
                                    <img
                                        src={footerLogoUrl}
                                        alt={businessName}
                                        style={{
                                            ...footerStyles.logo,
                                            filter: tokens.logoFilter,
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>

                        {!hideSocialBlock ? (
                            <div style={footerStyles.socialWrap}>
                                <SocialFooterBlock
                                    hideHandles
                                    socialMediaProfiles={socialMediaProfiles}
                                    googleDetails={googleDetails}
                                    tokens={tokens}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            <div style={tokens.bottom}>
                <div
                    className="footer-bottom-inner"
                    style={{
                        ...layoutTokens.container,
                        ...layoutTokens.bottomPadding,
                        ...footerStyles.bottomInner,
                    }}
                >
                    <p style={footerStyles.bottomText}>
                        {businessDetails.companyData?.licenseNumber ?? ''}
                    </p>

                    <p style={footerStyles.bottomText} title={businessTitle || ''}>
                        © {businessName} {year}
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
